// frontend/user/src/routes/forum.tsx
// 论坛页 LNMX9(原首页)— 文章流(UGC;无需登录)。
// 2026-06-05 module spec:/#/forum;feed/分类树锁 module=forum;/#/dating 本期占位页(dating.tsx)
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { cn } from '@dogdisk/shared'
import { MobileScreen } from '../components/mobile-screen'
import { TabBar } from '../components/tab-bar'
import { Pagination } from '../components/pagination'
import { ArticleCard } from '../components/article-card'
import { usePublicCategories } from '../_lib/discover-api'
import { useArticleFeed, type ArticleFeedSort } from '../_lib/article-api'
import { resolveFeedCategoryIds } from '../_lib/forum-category'
import { useDraggableFab } from '../_lib/use-draggable-fab'
import { AdIconGrid } from '../components/ad/ad-icon-grid'
import { AdCard, useForumCardAds } from '../components/ad/ad-card'
import { AD_CAP } from '../_lib/ad-slot'

/**
 * 论坛页 LNMX9(原首页) — 文章流(UGC;无需登录)。
 *
 * 2026-06-02(D1/D4):公开"发现"从以文件为中心迁到以文章为中心。首页主列表是
 * 已发布文章流(v_article_feed),顶部按分类筛(v_category_public);删公开文件/包发现。
 * 2026-06-04:对齐 ui/dogdisk-user.pen LNMX9 落地稿 — 两级分类导航(L1 文字 tab `bHAjg`
 * + L2 胶囊 chip `wrGqr`)、最热/最新排序 tab(`cgQjr`)、2 列瀑布流卡片(`GmTJO`)、
 * 「写文章」FAB(`lSyeS` → /article/create)。
 * spec docs/superpowers/specs/2026-06-02-articles-and-fileinfo-lockdown-design.md
 */

export function ForumPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const cats = usePublicCategories('forum')
  const fab = useDraggableFab()

  const tree = useMemo(() => cats.data ?? [], [cats.data])

  // URL = 单一真相源:`?category_id=<L1|L2 uuid>&sort=hot|new`。点击 tab 只改 URL(replace,
  // 不污染 history),刷新 / 分享链接 / 文章详情分类 chip 跳回(`/?category_id=`)都能还原选中态。
  // 修复:此前 l1/l2/sort 仅存 useState,切 tab 不改 URL,刷新即丢选中态回到默认首页。
  const categoryId = searchParams.get('category_id')
  const sort: ArticleFeedSort = searchParams.get('sort') === 'new' ? 'new' : 'hot'

  // 由 URL 的 category_id + 分类树还原两级高亮态(uuid 可能是 L1 或 L2 叶子;树未加载时降级全部)。
  const { l1Id, l2Id } = useMemo<{ l1Id: string | null; l2Id: string | null }>(() => {
    if (!categoryId) return { l1Id: null, l2Id: null }
    if (tree.some((l1) => l1.id === categoryId)) return { l1Id: categoryId, l2Id: null }
    const parent = tree.find((l1) => l1.children.some((c) => c.id === categoryId))
    return parent ? { l1Id: parent.id, l2Id: categoryId } : { l1Id: null, l2Id: null }
  }, [categoryId, tree])

  const activeL1 = useMemo(() => tree.find((l1) => l1.id === l1Id) ?? null, [tree, l1Id])
  // L2 胶囊(wrGqr)始终展示:选中具体 L1 → 其子类;L1=全部 → 全部一级的子类铺平。
  const l2Cats = useMemo(
    () => (activeL1 ? activeL1.children : tree.flatMap((l1) => l1.children)),
    [activeL1, tree],
  )

  // 最具体的选中分类驱动文章流。点 L1 → 聚合其全部 L2 子类(.in);L2 → 单 id;全部 → 不传。
  // 锁 module=forum。tree 未加载且有 category_id 时禁查,避免拿 L1 id 空查并被缓存(根因修复)。
  const feedCategoryIds = useMemo(() => resolveFeedCategoryIds(tree, categoryId), [tree, categoryId])

  // 分页:page 仅 forum 用(每页 20)。最热用 0 作「未分配起始页」哨兵 → 由后端按身份+日期算;
  // 最新从第 1 页起。切分类/排序(URL category_id+sort 变)即重置。
  const [page, setPage] = useState(() => (sort === 'hot' ? 0 : 1))
  useEffect(() => {
    setPage(sort === 'hot' ? 0 : 1)
  }, [categoryId, sort])

  const feed = useArticleFeed({
    module: 'forum',
    sort,
    page,
    // count 端点按单 category_id 收口;传 URL 选中的 categoryId(L1/L2 叶子)。
    ...(categoryId ? { categoryId } : {}),
    ...(feedCategoryIds ? { categoryIds: feedCategoryIds } : {}),
    enabled: !categoryId || tree.length > 0,
  })

  // 最热:page=0(未分配)时,采纳后端响应里的起始页 → 设进本地 page(分页器随之高亮)。
  // 这会触发一次 page:0→N 的 refetch(命中 Redis 快照,廉价;返回同一页内容)。
  useEffect(() => {
    if (sort === 'hot' && page === 0 && feed.data?.page) {
      setPage(feed.data.page)
    }
  }, [sort, page, feed.data?.page])

  // 写 URL:默认值(category 空 / sort=hot)从 query 删除,保持链接干净;replace 不堆 history。
  const patchParams = (patch: { category?: string | null; sort?: ArticleFeedSort }): void => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        if ('category' in patch) {
          if (patch.category) p.set('category_id', patch.category)
          else p.delete('category_id')
        }
        if (patch.sort) {
          if (patch.sort === 'new') p.set('sort', 'new')
          else p.delete('sort')
        }
        return p
      },
      { replace: true },
    )
  }

  // 选 L1 → category 设为该 L1(重置 L2);选 L2「全部」→ 回落当前 L1;选 L2 叶子 → 该叶子。
  const selectL1 = (id: string | null): void => patchParams({ category: id })
  const selectL2 = (id: string | null): void => patchParams({ category: id ?? l1Id })
  const selectSort = (s: ArticleFeedSort): void => patchParams({ sort: s })

  const articles = feed.data?.items ?? []
  const total = feed.data?.total ?? 0

  // 论坛卡片广告(forum_list_card):每 4 篇文章后插 1 张卡片广告,最多 4 张。
  const adVisible = useForumCardAds()
  const forumFeedNodes = useMemo(() => {
    let adIdx = 0
    return articles.flatMap((a, i) => {
      const articleNode = (
        <ArticleCard
          key={a.id}
          article={a}
          testId="LNMX9-article-card"
          onClick={() => void navigate(`/article/${a.id}`)}
        />
      )
      const shouldInsertAd = (i + 1) % 4 === 0 && adIdx < AD_CAP.forum_list_card
      if (shouldInsertAd) {
        const k = adIdx
        adIdx++
        const ad = adVisible[k]
        return [articleNode, <AdCard key={`ad-card-${k}`} {...(ad !== undefined ? { ad } : {})} position={k + 1} />]
      }
      return [articleNode]
    })
  }, [articles, adVisible, navigate])

  // 行优先读序的双列瀑布流:按下标奇偶轮流分入左右两列。
  // 扁平数组(文章 + 插播广告)已是正确的行优先序列,偶数入左、奇数入右,
  // 使视觉首行为 1,2、次行为 3,4。代价是两列底部可能有轻微落差(见设计文档)。
  const leftNodes = forumFeedNodes.filter((_, i) => i % 2 === 0)
  const rightNodes = forumFeedNodes.filter((_, i) => i % 2 === 1)

  return (
    <MobileScreen bg="white">
      {/* L1CategoryScroll(bHAjg)— L1 文字 tab + 分隔 + searchBtn */}
      <header className="flex items-center gap-2 pl-4 pt-[max(8px,env(safe-area-inset-top))] pb-1">
        <div className="flex flex-1 items-center gap-[18px] overflow-x-auto pr-2 scrollbar-none">
          <L1Tab label="全部" active={l1Id === null} onClick={() => selectL1(null)} />
          {cats.isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-5 w-9 shrink-0 animate-pulse rounded-sm bg-surface-200" />
              ))
            : tree.map((l1) => (
                <L1Tab
                  key={l1.id}
                  label={l1.name}
                  active={l1.id === l1Id}
                  onClick={() => selectL1(l1.id)}
                />
              ))}
        </div>
        <span className="h-6 w-px shrink-0 bg-surface-300" />
        <button
          type="button"
          aria-label="搜索"
          onClick={() => void navigate('/search')}
          className="mr-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-100 text-ink active:bg-surface-200"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-3.5-3.5" />
          </svg>
        </button>
      </header>

      {/* L2CategoryScroll(wrGqr)— 二级分类胶囊;始终展示(L1=全部 时铺平全部二级) */}
      {l2Cats.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto px-4 pb-2 pt-1 scrollbar-none">
          <L2Pill label="全部" active={l2Id === null} onClick={() => selectL2(null)} />
          {l2Cats.map((c) => (
            <L2Pill key={c.id} label={c.name} active={l2Id === c.id} onClick={() => selectL2(c.id)} />
          ))}
        </div>
      )}

      {/* SortTabRow(cgQjr)— 最热 / 最新 */}
      <div className="flex items-center gap-6 px-4 py-2">
        <SortTab label="最热" active={sort === 'hot'} onClick={() => selectSort('hot')} />
        <SortTab label="最新" active={sort === 'new'} onClick={() => selectSort('new')} />
      </div>

      {/* AdIconGrid(J2JB4h)— 5×4 小图标广告位(无外框,grid gap-7);px-3 让 slot 与瀑布流内容左右对齐 */}
      <div className="px-3 pb-2">
        <AdIconGrid />
      </div>

      {/* MasonryGrid(GmTJO)— 2 列瀑布流文章卡(轮流分列,行优先读序 1,2/3,4) */}
      <div data-testid="LNMX9" className="flex-1 overflow-y-auto px-3 pt-1 pb-safe-tab">
        {feed.isLoading ? (
          <div className="flex items-start gap-2">
            <div className="flex min-w-0 flex-1 flex-col">
              {[180, 150, 170].map((h, i) => (
                <div
                  key={i}
                  style={{ height: h }}
                  className="mb-2 w-full animate-pulse rounded-xl bg-surface-200"
                />
              ))}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              {[130, 110, 140].map((h, i) => (
                <div
                  key={i}
                  style={{ height: h }}
                  className="mb-2 w-full animate-pulse rounded-xl bg-surface-200"
                />
              ))}
            </div>
          </div>
        ) : feed.isError ? (
          <div className="flex flex-col items-center gap-3 pt-16">
            <p className="text-ink-mute">加载失败</p>
            <button type="button" className="rounded-full bg-danger-500 px-6 py-2 text-white" onClick={() => void feed.refetch()}>
              重试
            </button>
          </div>
        ) : articles.length === 0 ? (
          <div className="flex flex-col items-center gap-1 pt-16 text-ink-mute">
            <p className="text-[15px]">暂无文章</p>
            <p className="text-[13px]">换个分类试试</p>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <div className="flex min-w-0 flex-1 flex-col">{leftNodes}</div>
            <div className="flex min-w-0 flex-1 flex-col">{rightNodes}</div>
          </div>
        )}

        {total > 20 && (
          <Pagination
            page={page}
            total={total}
            pageSize={20}
            onChange={(p) => {
              setPage(p)
              document.querySelector('[data-testid="LNMX9"]')?.scrollTo({ top: 0 })
            }}
          />
        )}
      </div>

      {/* FAB-写文章(lSyeS)→ /article/create(路由 authed,未登录由守卫跳登录) */}
      <button
        type="button"
        data-testid="LNMX9-fab-write"
        aria-label="写文章"
        onClick={fab.guardClick(() => void navigate('/article/create'))}
        {...fab.dragProps}
        className="fixed z-20 flex h-14 w-14 cursor-grab touch-none items-center justify-center rounded-full bg-brand-500 text-white shadow-[0_8px_24px_rgba(25,202,173,0.45)] active:bg-brand-600 active:cursor-grabbing"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      </button>

      <TabBar />
    </MobileScreen>
  )
}

/** L1 文字 tab(选中:红字 + 下划线 / 未选:灰字)。 */
function L1Tab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex shrink-0 flex-col items-center gap-1">
      <span className={cn('text-[18px] transition-colors', active ? 'font-semibold text-danger-500' : 'font-normal text-ink-mute')}>
        {label}
      </span>
      <span className={cn('h-[3px] w-[18px] rounded-sm', active ? 'bg-danger-500' : 'bg-transparent')} />
    </button>
  )
}

/** L2 胶囊 chip(选中:品牌色填充 / 未选:浅灰底)。 */
function L2Pill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-[14px] px-3 py-1 text-[12px] font-semibold transition-colors',
        active ? 'bg-brand-500 text-white' : 'bg-surface-100 text-ink-muted',
      )}
    >
      {label}
    </button>
  )
}

/** 排序 tab(选中:黑体 + 蓝下划线 / 未选:灰字)。 */
function SortTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-1">
      <span className={cn('text-[16px] transition-colors', active ? 'font-bold text-ink' : 'font-medium text-ink-mute')}>
        {label}
      </span>
      <span className={cn('h-[3px] w-5 rounded-sm', active ? 'bg-info-600' : 'bg-transparent')} />
    </button>
  )
}
